/**
 * Looks at the accession number, object number, and manuscript number to see if the queryTerm matches
 *
 * @param {string} queryTerm - the search term
 * @param {object} result  - a search result
 *
 * @returns {boolean} - true if it matches
 */
export function accessionNumberMatches(queryTerms, result) {
  let queryTerm = queryTerms;
  if (Array.isArray(queryTerm) && queryTerm.length > 0) {
    queryTerm = queryTerms[0];
  }
  if (
    queryTerm == undefined ||
    result == undefined ||
    typeof queryTerm !== "string"
  ) {
    return false;
  }
  let query = normalizeLookupText(queryTerm);

  if (
    (query.startsWith("'") && query.endsWith("'")) ||
    (query.startsWith('"') && query.endsWith('"'))
  ) {
    query = query.substring(1, query.length - 1);
  }
  if (
    query == normalizeLookupText(result.accession_number) ||
    query == normalizeLookupText(result.manuscript_number) ||
    query == normalizeLookupText(result.object_number)
  ) {
    return true;
  }
  return false;
}

/**
 * Get chip terms objects for advanced searchbox and mobile overlay
 *
 * @param {object} currentFilters - search filter objects
 * @param {array} filtersList - array of filter fieldnames from the filterBar
 * @param {function} translate - i18n translate function
 * @param {object} chipTermIdValues - { indexed_id: "primary_name" }
 *
 * @returns {Array} chipObjects - array of chip term objects
 */
export function getChipObjects(
  currentFilters,
  filtersList,
  translate,
  chipTermIdValues = {}
) {
  let chipObjects = [];

  for (let filter in currentFilters) {
    // filter terms not to display
    if (["sort", "seed"].includes(filter)) continue;

    currentFilters[filter].forEach((value) => {
      let chipObject = { qualifier: filter, term: value };
      if (filtersList.includes(filter)) {
        // don't set chip if excluding deaccessioned (default behavior)
        if (filter == "deaccessioned" && value == "false") {
          return;
        }
        // When open_content is false replace the translation with
        // permission_required translation
        if (filter == "open_content" && value == "false") {
          chipObject.term = "permission_required";
        } else {
          chipObject.term = filter;
        }
        chipObject.termValue = value;
        chipObject.showQualifier = false;
      }

      if (filter == "date_range" && String(value).indexOf(":") > -1) {
        chipObject.termValue = value;
        let values = value.split(":");
        values = values.map((val) => {
          let newVal = parseInt(val);
          if (newVal < 0) {
            newVal = translate("date.bce_date", { date: Math.abs(newVal) });
          }
          return newVal;
        });
        chipObject.term = `${values[0]} – ${values[1]}`;
      }

      // use unknown artist translation
      if (filter == "artist" && value == "Unknown") {
        chipObject.termValue = value;
        chipObject.term = translate("artist.unknown");
      }

      // for filters with ids, return primary name instead
      if (chipTermIdValues[value]) {
        chipObject.termValue = value;
        chipObject.term = chipTermIdValues[value];
      }

      chipObjects.push(chipObject);
    });
  }
  return chipObjects;
}

/**
 * Gets the "View" param and returns the default value if it's not a string, not defined, or not 4 charaters
 * @param {String} initial_value
 * @param {Number} default_value
 *
 * @returns {Number}
 */
export function getViewParam(initial_value, default_value) {
  if (
    initial_value == undefined ||
    typeof initial_value != String ||
    initial_value.length != 4
  ) {
    return default_value;
  } else {
    return initial_value;
  }
}

/**
 * Removes a bunch of string parameters to help normalize text for matching
 *
 * @param {String} string
 *
 * @returns  {string}
 */
export function normalizeLookupText(string) {
  if (string == undefined) {
    return undefined;
  }
  let str = string.replace(/[\s\,\.\;_\-\:\[\(\]\)\{\}\]]/gi, "");
  return str.toLowerCase().trim();
}
